// src/components/profile/VAProfileSetup.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiUser, FiMail, FiPhone, FiMapPin, FiClock, FiDollarSign,
  FiPlus, FiX, FiUpload, FiSave, FiEye, FiStar, FiGlobe
} from 'react-icons/fi';
import { Sparkles, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';

const VAProfileSetup = () => {
  const navigate = useNavigate();
  const { userProfile, updateUserProfile } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [profileData, setProfileData] = useState({
    // Basic Information
    displayName: '',
    email: '',
    phone: '',
    location: '',
    timezone: '',
    profilePhoto: '',
    
    // Professional Information  
    title: '',
    bio: '',
    experience: '',
    languages: [],
    
    // Skills and Services
    skills: [],
    services: [],
    
    // Rates and Availability
    hourlyRate: '',
    currency: 'ZAR',
    availability: 'available', // available, busy, unavailable
    hoursPerWeek: '',
    
    // Portfolio and Certifications
    portfolio: [],
    certifications: [],
    
    // Profile Settings
    isPublic: true,
    instantBooking: false,
    profileComplete: false
  });

  const availableSkills = [
    'Administrative Support', 'Data Entry', 'Customer Service', 'Email Management',
    'Calendar Management', 'Social Media Management', 'Content Writing', 'Research',
    'Bookkeeping', 'Lead Generation', 'Virtual Reception', 'Project Management',
    'Graphic Design', 'Web Development', 'SEO', 'Digital Marketing',
    'Translation', 'Transcription', 'Event Planning', 'E-commerce Support'
  ];

  const serviceTemplates = [
    { name: 'Email Management', rate: 250, unit: 'hour' },
    { name: 'Data Entry', rate: 200, unit: 'hour' },
    { name: 'Customer Support', rate: 300, unit: 'hour' },
    { name: 'Social Media Management', rate: 400, unit: 'hour' },
    { name: 'Content Writing', rate: 500, unit: 'hour' },
    { name: 'Research Tasks', rate: 350, unit: 'hour' },
    { name: 'Administrative Support', rate: 280, unit: 'hour' }
  ];

  const timezones = [
    'Africa/Johannesburg', 'Africa/Cape_Town', 'Africa/Cairo',
    'Europe/London', 'Europe/Berlin', 'America/New_York',
    'America/Los_Angeles', 'Asia/Dubai', 'Asia/Singapore'
  ];

  const languages = [
    'English', 'Afrikaans', 'Zulu', 'Xhosa', 'French', 'German',
    'Spanish', 'Portuguese', 'Arabic', 'Mandarin'
  ];

  useEffect(() => {
    loadVAProfile();
  }, [userProfile]);

  const loadVAProfile = async () => {
    if (!userProfile?.uid) return;
    
    try {
      setLoading(true);
      const vaProfile = await jobService.getVAProfile(userProfile.uid);
      
      if (vaProfile) {
        setProfileData({
          ...profileData,
          ...vaProfile,
          displayName: vaProfile.displayName || userProfile.displayName || '',
          email: vaProfile.email || userProfile.email || ''
        });
      } else {
        // Initialize with user data
        setProfileData(prev => ({
          ...prev,
          displayName: userProfile.displayName || '',
          email: userProfile.email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading VA profile:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayAdd = (field, value) => {
    if (!value.trim()) return;
    setProfileData(prev => ({
      ...prev,
      [field]: [...prev[field], value]
    }));
  };

  const handleArrayRemove = (field, index) => {
    setProfileData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleServiceAdd = (service) => {
    const exists = profileData.services.find(s => s.name === service.name);
    if (exists) return;
    
    setProfileData(prev => ({
      ...prev,
      services: [...prev.services, { ...service, id: Date.now() }]
    }));
  };

  const handleServiceUpdate = (serviceId, field, value) => {
    setProfileData(prev => ({
      ...prev,
      services: prev.services.map(service =>
        service.id === serviceId ? { ...service, [field]: value } : service
      )
    }));
  };

  const handleServiceRemove = (serviceId) => {
    setProfileData(prev => ({
      ...prev,
      services: prev.services.filter(service => service.id !== serviceId)
    }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setSaving(true);
      const photoUrl = await jobService.uploadVAPhoto(userProfile.uid, file);
      setProfileData(prev => ({ ...prev, profilePhoto: photoUrl }));
    } catch (error) {
      setError('Failed to upload photo');
    } finally {
      setSaving(false);
    }
  };

  const validateProfile = () => {
    const required = ['displayName', 'title', 'bio', 'location', 'hourlyRate'];
    const missing = required.filter(field => !profileData[field]);
    
    if (missing.length > 0) {
      setError(`Please fill in: ${missing.join(', ')}`);
      return false;
    }
    
    if (profileData.skills.length === 0) {
      setError('Please add at least one skill');
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateProfile()) return;

    try {
      setSaving(true);
      setError(null);

      const updatedProfile = {
        ...profileData,
        profileComplete: true,
        updatedAt: new Date().toISOString()
      };

      await jobService.updateVAProfile(userProfile.uid, updatedProfile);
      await updateUserProfile({ role: 'va' });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/profile/view');
      }, 2000);
      
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: profileData.currency
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="text-green-600" size={32} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Saved Successfully!</h2>
        <p className="text-gray-600 mb-6">Your VA profile is now complete and visible to clients.</p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={() => navigate('/profile/view')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            View Profile
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">VA Profile Setup</h1>
          <p className="text-gray-600 mt-2">Create your professional profile to attract clients</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate('/profile/view')}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
          >
            <FiEye className="mr-2" size={16} />
            Preview
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave className="mr-2" size={16} />
                Save Profile
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <FiUser className="mr-2" size={20} />
              Basic Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={profileData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Professional Title *
                </label>
                <input
                  type="text"
                  value={profileData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Virtual Assistant, Data Specialist"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your.email@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+27 123 456 7890"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  value={profileData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="City, Country"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={profileData.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select timezone</option>
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Professional Bio *
              </label>
              <textarea
                value={profileData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Describe your experience, expertise, and what makes you unique as a virtual assistant..."
              />
              <p className="text-sm text-gray-500 mt-1">
                {profileData.bio.length}/500 characters
              </p>
            </div>
          </div>

          {/* Skills & Services */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Sparkles className="mr-2" size={20} />
              Skills & Services
            </h3>
            
            {/* Skills */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Your Skills *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                {availableSkills.map(skill => (
                  <button
                    key={skill}
                    onClick={() => {
                      if (profileData.skills.includes(skill)) {
                        handleArrayRemove('skills', profileData.skills.indexOf(skill));
                      } else {
                        handleArrayAdd('skills', skill);
                      }
                    }}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      profileData.skills.includes(skill)
                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                        : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
              
              {profileData.skills.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {profileData.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-700"
                    >
                      {skill}
                      <button
                        onClick={() => handleArrayRemove('skills', index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <FiX size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            
            {/* Services */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Services & Rates
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {serviceTemplates.map(service => (
                  <button
                    key={service.name}
                    onClick={() => handleServiceAdd(service)}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium">{service.name}</span>
                    <span className="text-sm text-gray-600">
                      {formatCurrency(service.rate)}/{service.unit}
                    </span>
                  </button>
                ))}
              </div>
              
              {profileData.services.length > 0 && (
                <div className="space-y-3">
                  {profileData.services.map(service => (
                    <div key={service.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <input
                          type="text"
                          value={service.name}
                          onChange={(e) => handleServiceUpdate(service.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          value={service.rate}
                          onChange={(e) => handleServiceUpdate(service.id, 'rate', parseInt(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <span className="text-sm text-gray-600">/{service.unit}</span>
                      <button
                        onClick={() => handleServiceRemove(service.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Rates & Availability */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <FiDollarSign className="mr-2" size={20} />
              Rates & Availability
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Base Hourly Rate *
                </label>
                <div className="relative">
                  <select
                    value={profileData.currency}
                    onChange={(e) => handleInputChange('currency', e.target.value)}
                    className="absolute left-3 top-3 border-none bg-transparent focus:outline-none text-gray-600"
                  >
                    <option value="ZAR">ZAR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                  <input
                    type="number"
                    value={profileData.hourlyRate}
                    onChange={(e) => handleInputChange('hourlyRate', e.target.value)}
                    className="w-full pl-16 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="250"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Availability
                </label>
                <select
                  value={profileData.availability}
                  onChange={(e) => handleInputChange('availability', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="available">Available</option>
                  <option value="busy">Busy</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hours per Week
                </label>
                <input
                  type="number"
                  value={profileData.hoursPerWeek}
                  onChange={(e) => handleInputChange('hoursPerWeek', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="40"
                />
              </div>
            </div>
          </div>

          {/* Languages */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <FiGlobe className="mr-2" size={20} />
              Languages
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {languages.map(language => (
                <button
                  key={language}
                  onClick={() => {
                    if (profileData.languages.includes(language)) {
                      handleArrayRemove('languages', profileData.languages.indexOf(language));
                    } else {
                      handleArrayAdd('languages', language);
                    }
                  }}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    profileData.languages.includes(language)
                      ? 'bg-green-100 border-green-300 text-green-700'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {language}
                </button>
              ))}
            </div>
            
            {profileData.languages.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {profileData.languages.map((language, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-700"
                  >
                    {language}
                    <button
                      onClick={() => handleArrayRemove('languages', index)}
                      className="ml-2 text-green-600 hover:text-green-800"
                    >
                      <FiX size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Profile Photo */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Profile Photo</h4>
            
            <div className="text-center">
              {profileData.profilePhoto ? (
                <div className="relative inline-block">
                  <img
                    src={profileData.profilePhoto}
                    alt="Profile"
                    className="w-32 h-32 rounded-full object-cover"
                  />
                  <button
                    onClick={() => handleInputChange('profilePhoto', '')}
                    className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 mx-auto bg-gray-200 rounded-full flex items-center justify-center">
                  <FiUser className="text-gray-400" size={48} />
                </div>
              )}
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                className="hidden"
              />
              
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={saving}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center mx-auto"
              >
                <FiUpload className="mr-2" size={16} />
                {saving ? 'Uploading...' : 'Upload Photo'}
              </button>
            </div>
          </div>

          {/* Profile Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Profile Settings</h4>
            
            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={profileData.isPublic}
                  onChange={(e) => handleInputChange('isPublic', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm text-gray-700">
                  Make profile public
                </span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={profileData.instantBooking}
                  onChange={(e) => handleInputChange('instantBooking', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm text-gray-700">
                  Allow instant booking
                </span>
              </label>
            </div>
          </div>

          {/* Progress */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Profile Completion</h4>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full mr-3 ${
                  profileData.displayName && profileData.title && profileData.bio 
                    ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <span className="text-sm text-gray-700">Basic Information</span>
              </div>
              
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full mr-3 ${
                  profileData.skills.length > 0 
                    ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <span className="text-sm text-gray-700">Skills & Services</span>
              </div>
              
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full mr-3 ${
                  profileData.hourlyRate 
                    ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <span className="text-sm text-gray-700">Rates & Availability</span>
              </div>
              
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded-full mr-3 ${
                  profileData.profilePhoto 
                    ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <span className="text-sm text-gray-700">Profile Photo</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VAProfileSetup;